import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DrawerNoHeader } from '../../components/drawer/DrawerNoHeader';
import { Button } from '../../components/buttons/Button';
import { AvatarWithInitials } from '../../components/avatar/AvatarWithInitials';
import { Loading } from '../../components/loading/Loading';
import { formatDate, formatDateTime } from '../../common/formatDateText';
import { getRequestInvoiceDetails } from '../../redux/actions/request';
import { ImageZoom } from '../../components/imageZoom/ImageZoom';
import { PaymentHistoryTimeline } from '../../components/paymentHistory/PaymentHistoryTimeline';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';

export const DrawerRequestInvoicePaymentDetails = ({
	isOpen,
	toggleDrawer,
	requestId,
}) => {
	const dispatch = useDispatch();
	const { requestInvoiceSelected, loadingRequestInvoiceSelected } = useSelector((state) => state.request);
	const [activeMenuOption, setActiveMenuOption] = useState(null);

	useEffect(() => {
		if (!requestId || !isOpen) return;
		dispatch(getRequestInvoiceDetails(requestId));
	}, [dispatch, requestId, isOpen]);

	useEffect(() => {
		if (!isOpen) setActiveMenuOption(null);
	}, [isOpen]);

	const paymentData = requestInvoiceSelected?.payment || {};
	const requestBy = requestInvoiceSelected?.requestBy || paymentData?.user || {};
	const paymentUser = paymentData?.user || requestBy || {};
	const account = paymentData?.account || {};
	const history = Array.isArray(paymentData?.history) ? paymentData.history : [];
	const lastValidatedEntry = [...history].reverse().find((entry) => entry?.action === 'validado');
	const approvedByRaw = requestInvoiceSelected?.approvedBy || paymentData?.approvedBy || lastValidatedEntry?.byWho || {};

	const fullName = `${requestBy?.firstName || ''} ${requestBy?.secondName || ''} ${requestBy?.lastName || ''} ${requestBy?.secondSurname || ''}`.trim();
	const approvedByName = typeof approvedByRaw === 'object'
		? `${approvedByRaw?.firstName || ''} ${approvedByRaw?.secondName || ''} ${approvedByRaw?.lastName || ''} ${approvedByRaw?.secondSurname || ''}`.trim()
		: approvedByRaw;
	const isAuxPanelOpen = Boolean(activeMenuOption);

	const handleSelectMenuOption = (optionId) => {
		setActiveMenuOption((prev) => (prev === optionId ? null : optionId));
	};
	return (
		<DrawerNoHeader
			isOpen={isOpen}
			toggleDrawer={toggleDrawer}
			direction='right'
			menuBar={{
				enabled: true,
				activeId: activeMenuOption,
				onSelect: handleSelectMenuOption,
				buttons: [
					{
						id: 'aux-space',
						title: isAuxPanelOpen ? 'Ocultar comprobante de pago' : 'Mostrar comprobante de pago',
						icon: isAuxPanelOpen
							? <ArrowsPointingInIcon className='h-6 w-6' aria-hidden />
							: <ArrowsPointingOutIcon className='h-6 w-6' aria-hidden />,
					},
				],
			}}
			menuExpand={{
				enabled: isOpen,
				isExpanded: isAuxPanelOpen,
				sizeLG: '75%',
				sizeXL: '70%',
				sizeXXL: '60%',
			}}
			footer={(
				<div className='flex items-center justify-end gap-4'>
					<Button type="button" variant="secondary" onClick={toggleDrawer}>
						Cerrar
					</Button>
				</div>
			)}
		>
			<div className='w-full h-full flex flex-col overflow-hidden'>
				<div className='w-full bg-white rounded-lg border border-gray-200 flex-shrink-0'>
					<div className='bg-gradient-to-r from-indigo-500 to-purple-600 p-5 md:p-6 rounded-tr-lg'>
						<div className='flex items-center gap-4 md:gap-5'>
							<div className='relative flex-shrink-0'>
								<AvatarWithInitials
									firstName={requestBy?.firstName || ''}
									lastName={requestBy?.lastName || ''}
									loading={loadingRequestInvoiceSelected}
									size="md"
								/>
							</div>
							<div className='flex-1 min-w-0'>
								<h3 className='text-white font-bold text-lg md:text-xl lg:text-2xl capitalize tracking-tight'>
									{fullName || 'Usuario'}
								</h3>
								<div className='mt-2 flex flex-wrap gap-2'>
									{requestBy?.matricula && (
										<div className='inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/30'>
											<span className='text-white text-sm font-semibold'>
												{requestBy?.matricula}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className='flex-1 overflow-hidden p-4 md:p-5'>
					{loadingRequestInvoiceSelected ? (
						<Loading />
					) : (
						<div className='h-full flex gap-4'>
							{isAuxPanelOpen && (
								<div className='hidden xl:flex shrink-0'>
									<div className='w-[25vw] rounded-lg bg-white transition-all duration-200'>
										<div className='h-[border-r border-gray-300/80 overflow-auto'>
											<div className='bg-white border border-gray-200 rounded-lg p-5'>
												<div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-100'>
													<h4 className='text-lg font-bold text-gray-800'>Comprobante de pago</h4>
												</div>
												<div className='w-full flex flex-col justify-center items-center py-4 rounded-lg'>
													{paymentData?.urlName ? (
														<ImageZoom imageUrl={paymentData.urlName} />
													) : (
														<div className='w-full flex justify-center items-center min-h-[280px] px-4'>
															<span className='text-gray-600 text-center'>
																No se ha subido un comprobante de pago
															</span>
														</div>
													)}
												</div>
											</div>
										</div>
								</div>
							</div>
							)}
							<div className='flex-1 overflow-auto pr-1'>
								<div className='flex flex-col gap-5'>
							<div className="bg-white border border-gray-200 rounded-lg p-5">
								<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
									<h4 className="text-lg font-bold text-gray-800">Datos fiscales</h4>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Razón social</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentUser?.businessName || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">RFC</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentUser?.rfc || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Correo fiscal</label>
										<p className="text-[.9rem] font-medium text-gray-900">{paymentUser?.emailTax || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Régimen fiscal</label>
										<p className="text-[.9rem] font-medium text-gray-900">{paymentUser?.taxRegime || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Correo personal</label>
										<p className="text-[.9rem] font-medium text-gray-900">{paymentUser?.email || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Teléfono</label>
										<p className="text-[.9rem] font-medium text-gray-900">{paymentUser?.phone || '-'}</p>
									</div>
								</div>
							</div>
							<div className="bg-white border border-gray-200 rounded-lg p-5">
								<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
									<h4 className="text-lg font-bold text-gray-800">Información del pago</h4>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Folio</label>
										<p className="text-[.9rem] font-medium text-gray-900">{paymentData?.paymentFolio ? `#${paymentData.paymentFolio}` : '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Tipo de pago</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentData?.paymentType || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Monto</label>
										<p className="text-[.9rem] font-medium text-gray-900">{`$ ${paymentData?.amount || 0}`}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Estatus</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentData?.status || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Fecha de registro</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentData?.createdAt ? formatDateTime(paymentData.createdAt) : '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Última actualización</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentData?.updatedAt ? formatDateTime(paymentData.updatedAt) : '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Fecha de recibo</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentData?.paymentReceiptDate ? formatDate(paymentData.paymentReceiptDate) : '-'}</p>
									</div>
								</div>
							</div>

							<div className="bg-white border border-gray-200 rounded-lg p-5">
								<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
									<h4 className="text-lg font-bold text-gray-800">Información de la cuenta</h4>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Nombre cuenta</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{account?.nameAccount || '-'}</p>
									</div>
									<div className="space-y-1">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Número cuenta</label>
										<p className="text-[.9rem] font-medium text-gray-900 uppercase">{account?.numberAccount || '-'}</p>
									</div>
									<div className="space-y-1 md:col-span-2">
										<label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Aprobada por</label>
										<p className="text-[.9rem] font-medium text-gray-900 capitalize">{approvedByName || '-'}</p>
									</div>
								</div>
							</div>

							{!isAuxPanelOpen && (
								<div className="bg-white border border-gray-200 rounded-lg p-5">
									<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
										<h4 className="text-lg font-bold text-gray-800">Comprobante de pago</h4>
									</div>
									<div className='w-full flex flex-col justify-center items-center py-4 border-dashed border-spacing-4 border-2 border-gray-200 rounded-lg'>
										{paymentData?.urlName ? (
											<ImageZoom imageUrl={paymentData.urlName} />
										) : (
											<div className='w-full flex justify-center items-center min-h-[280px] md:min-h-[400px] px-4'>
												<span className='text-gray-600 text-center'>
													No se ha subido un comprobante de pago
												</span>
											</div>
										)}
									</div>
								</div>
							)}

							{history.length > 0 && (
								<div className="bg-white border border-gray-200 rounded-lg p-5">
									<div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
										<h4 className="text-lg font-bold text-gray-800">Historial de movimientos</h4>
									</div>
									<PaymentHistoryTimeline history={history} />
								</div>
							)}
						</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</DrawerNoHeader>
	);
};
